import styled from "styled-components";
import {
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
  PRIMARY_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import {
  BORDER_RADIUS,
  SPACING,
} from "../../../constants/design/spacing";
import { InputWrapper } from "../style.info";

/* ── New trade form layout components ─────────────────────────── */

export const TopButtonsRowWrapper = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  padding: ${SPACING.three}px ${SPACING.three}px 0;
`;

export const TopBtn = styled.button`
  flex: 1;
  min-width: 0;
  height: 34px;
  border-radius: 8px;
  border: 1px solid ${BORDER_COLORS.three};
  background: ${LAYER_COLORS.two};
  color: ${TEXT_COLORS.one};
  font-size: ${FONT_SIZE.one};
  white-space: nowrap;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${BORDER_COLORS.four};
  }
`;

export const OrderTypeTabsWrapper = styled.div`
  display: flex;
  border-bottom: 1px solid ${BORDER_COLORS.two};
  margin-top: ${SPACING.three}px;
`;

export const OrderTypeTabBtn = styled.button<{ $active?: boolean }>`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  background: transparent;
  border: none;
  border-bottom: 2px solid
    ${({ $active }) => ($active ? COLORS.highlight.one : "transparent")};
  color: ${({ $active }) => ($active ? TEXT_COLORS.one : TEXT_COLORS.three)};
  padding: ${SPACING.twoHalf}px ${SPACING.two}px;
  font-size: ${FONT_SIZE.one};
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.one}px;

  &:hover {
    color: ${TEXT_COLORS.one};
  }
`;

export const AccountInfoRowsWrapper = styled.div`
  padding: ${SPACING.two}px ${SPACING.three}px;
`;

export const AccountInfoRowItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${SPACING.two}px;
  padding: 2px 0;
`;

export const AccountInfoLabelText = styled.span`
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.three};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AccountInfoValueText = styled.span`
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.one};
  white-space: nowrap;
  flex-shrink: 0;
`;

/**
 * "Available to Trade" when clicking it fills MAX: the same figure, marked as
 * actionable the way the numeric-field kit marks a clickable balance
 * (NumericField `MetaValue`) — a dotted underline, the accent on hover.
 */
export const AccountInfoValueButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.one};
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  text-decoration: underline dotted ${TEXT_COLORS.three};
  text-underline-offset: 3px;
  transition: color 80ms ease;

  &:hover,
  &:focus-visible {
    color: ${PRIMARY_COLORS.one};
    text-decoration-color: ${PRIMARY_COLORS.one};
  }
`;

export const CheckboxRowsWrapper = styled.div`
  padding: ${SPACING.two}px 0 0;
`;

export const ReduceOnlyCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.one}px 0;
`;

export const CheckboxLabelWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
  cursor: pointer;
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.one};

  input[type="checkbox"] {
    width: 14px;
    height: 14px;
    cursor: pointer;
    accent-color: ${COLORS.highlight.one};
    flex-shrink: 0;
  }
`;

/**
 * TP/SL checkbox row. Matches ReduceOnlyCheckboxRow's vertical rhythm so the two
 * checkboxes sit on the same cadence.
 */
export const TpslCheckboxLabel = styled(CheckboxLabelWrapper)`
  padding: ${SPACING.one}px 0;
`;

/** Same row on chains with no trigger executor — visibly inert. */
export const TpslCheckboxLabelDisabled = styled(TpslCheckboxLabel)`
  opacity: 0.4;
  cursor: not-allowed;

  input[type="checkbox"] {
    cursor: not-allowed;
  }
`;

/**
 * The TP and SL trigger-price inputs, side by side. The bottom padding matters:
 * without it the inputs butt straight up against the One-Click Trading panel
 * that follows them.
 */
export const TpslInputsRow = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  width: 100%;
  padding: ${SPACING.two}px 0 ${SPACING.twoHalf}px;
`;

/**
 * One half of that row. `flex: 1` + `min-width: 0` rather than `width: 100%`, so
 * the two cells share the row evenly instead of relying on flex-shrink to undo an
 * overflow.
 */
export const TpslInputCell = styled(InputWrapper)`
  flex: 1;
  min-width: 0;
`;

export const TIFDropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.one}px;
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.three};
  cursor: pointer;

  span {
    color: ${TEXT_COLORS.one};
  }
`;

export const TradeInfoBottomSection = styled.div`
  padding: ${SPACING.two}px 0 ${SPACING.one}px;
`;

export const TradeInfoBottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
`;

export const TradeInfoBottomLabel = styled.span<{ $underline?: boolean }>`
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.three};
  text-decoration: ${({ $underline }) =>
    $underline ? "underline dotted" : "none"};
  cursor: ${({ $underline }) => ($underline ? "pointer" : "default")};
`;

export const TradeInfoBottomValue = styled.span`
  font-size: ${FONT_SIZE.one};
  color: ${TEXT_COLORS.one};
`;

export const DepositBtn = styled.button`
  width: 100%;
  height: 48px;
  border-radius: ${BORDER_RADIUS.two}px;
  border: none;
  background: ${COLORS.highlight.one};
  color: ${COLORS.highlight.on};
  font-size: ${FONT_SIZE.two};
  font-weight: 600;
  cursor: pointer;
  margin-top: ${SPACING.three}px;

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ActionBtnsRow = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  margin-top: ${SPACING.two}px;
`;

export const OutlineActionBtn = styled.button`
  flex: 1;
  height: 42px;
  border-radius: ${BORDER_RADIUS.two}px;
  border: 1px solid ${COLORS.highlight.two};
  background: transparent;
  color: ${COLORS.highlight.one};
  font-size: ${FONT_SIZE.one};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${COLORS.highlight.six};
  }
`;

export const AccountEquitySectionWrapper = styled.div`
  padding: ${SPACING.three}px;
`;

export const SectionTitleText = styled.div`
  font-size: ${FONT_SIZE.two};
  color: ${TEXT_COLORS.one};
  font-weight: 600;
  margin-bottom: 6px;
`;

export const SectionInfoRowItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
`;

export const SectionInfoLabelText = styled.span<{
  $underline?: boolean;
  $teal?: boolean;
}>`
  font-size: ${FONT_SIZE.one};
  color: ${({ $teal }) => ($teal ? COLORS.highlight.one : TEXT_COLORS.three)};
  text-decoration: ${({ $underline }) =>
    $underline ? "underline dotted" : "none"};
  cursor: ${({ $underline }) => ($underline ? "pointer" : "default")};
`;

export const SectionInfoValueText = styled.span<{ $teal?: boolean }>`
  font-size: ${FONT_SIZE.one};
  color: ${({ $teal }) => ($teal ? COLORS.highlight.one : TEXT_COLORS.one)};
`;
